import { Navigate } from 'react-router-dom';
import { VIDEO_URLS } from '@carcareers/utils';
import {
  AppShell,
  DashboardShell,
  DefaultShell,
  DefaultErrorBoundary,
  DashboardErrorBoundary,
  SentryDebug,
  MigrationDebug,
  EmployerDemo,
  RequestDebug,
} from '@carcareers/components';
import {
  JobSearch,
  JobIndex,
  JobView,
  Login,
  CreateJobseeker,
  NotFound,
  Home,
  Terms,
  Privacy,
  Employers,
  Rooftop,
  AdminHome,
  EmployerHome,
  EmployerIndex,
  EmployerView,
  JobseekerIndex,
  CampaignIndex,
  RooftopIndex,
  RooftopView,
  ResumeIndex,
  PromoIndex,
  PostIndex,
  EmployerPostIndex,
  SettingsIndex,
  JobseekerView,
  PromoView,
  EmployerSettings,
  CampaignView,
  PostView,
  EmployerJobIndex,
  EmployerJobAdd,
  EmployerRooftopIndex,
  EmployerRooftopView,
  PostRequestIndex,
  ContactRequestIndex,
  EmployerPostView,
  EmployerJobseekerIndex,
  ApplicationView,
  EmployerJobseekerView,
  CreateEmployer,
  EmployerChat,
  JobseekerChat,
  ForgotPassword,
  JobseekerSettings,
  JobseekerProfile,
  JobseekerApplications,
  JobseekerSavedPosts,
  EmployerJobseekerResumeView,
  ResumeView,
  PostAdd,
  EmployerJobView,
  GoExcelsus,
} from '@carcareers/pages';

import { sentryRouter } from './sentry';

export enum noAuthRoutes {
  HOME = '/',
  JOBSEEKERS = 'jobs',
  JOB_VIEW = 'jobs/:id',
  EMPLOYERS = 'employers',
  LOGIN = 'auth/login',
  JOBSEEKER_SIGNUP = 'auth/signup/jobseeker',
  EMPLOYER_SIGNUP = 'auth/signup/employer',
  FORGOT_PASSWORD = 'auth/reset-password',
  ROOFTOP = 'rooftop/:id',
  TERMS = 'terms',
  PRIVACY = 'privacy',
  GO_EXCELSUS = 'go/excelsus',
}

export enum employerRoutes {
  EMPLOYER_HOME = 'employer',
  EMPLOYER_SETTINGS = 'settings',

  EMPLOYER_JOBS_INDEX = 'templates',
  EMPLOYER_JOBS_ADD = 'templates/add',
  EMPLOYER_JOBS_VIEW = 'templates/:id',

  EMPLOYER_CANDIDATES_INDEX = 'candidates',
  EMPLOYER_CANDIDATES_VIEW = 'candidates/:id',
  EMPLOYER_CANDIDATES_RESUME_VIEW = 'candidates/:id/resume',

  EMPLOYER_POSTS_INDEX = 'jobs',
  EMPLOYER_POSTS_VIEW = 'jobs/:id',
  EMPLOYER_APPLICATIONS_VIEW = 'jobs/:id/:applicationId',

  EMPLOYER_REPORTS = 'reports',

  EMPLOYER_ROOFTOPS_INDEX = 'rooftops',
  EMPLOYER_ROOFTOPS_VIEW = 'rooftops/:id',
  EMPLOYER_ROOFTOPS_ADD = 'rooftops/add',
  EMPLOYER_POST_A_JOB = 'post-a-job',

  EMPLOYER_CHAT = 'messages',
  EMPLOYER_CHAT_VIEW = 'messages/:id',

  EMPLOYER_DEMO_POST_A_JOB = 'tutorial/post-a-job',
  EMPLOYER_DEMO_REQUEST_RECRUITER_ASSISTANCE = 'tutorial/request-a-recruiter',
  EMPLOYER_DEMO_MANAGE_CANDIDATES = 'tutorial/manage-candidates',
}

export enum adminRoutes {
  ADMIN_HOME = 'admin',

  ADMIN_JOBS = 'templates',
  ADMIN_JOBS_ADD = 'templates/add',
  ADMIN_JOBS_VIEW = 'templates/:id',

  ADMIN_EMPLOYERS = 'employers',
  ADMIN_EMPLOYERS_VIEW = 'employers/:id',
  ADMIN_EMPLOYERS_ADD = 'employers/add',

  ADMIN_JOBSEEKERS = 'jobseekers',
  ADMIN_JOBSEEKERS_ADD = 'jobseekers/add',
  ADMIN_JOBSEEKERS_VIEW = 'jobseekers/:id',

  ADMIN_CAMPAIGNS = 'campaigns',
  ADMIN_CAMPAIGNS_VIEW = 'campaigns/:id',

  ADMIN_ROOFTOPS = 'rooftops',
  ADMIN_ROOFTOPS_VIEW = 'rooftops/:id',

  ADMIN_REPORTS = 'reports',

  ADMIN_RESUMES = 'resumes',
  ADMIN_RESUMES_VIEW = 'resumes/:id',

  ADMIN_PROMOS = 'promos',
  ADMIN_PROMOS_VIEW = 'promos/:id',

  ADMIN_POSTS = 'jobs',
  ADMIN_POSTS_ADD = 'jobs/add',
  ADMIN_POSTS_VIEW = 'jobs/:id',
  ADMIN_APPLICATIONS_VIEW = 'jobs/:id/:applicationId',

  ADMIN_POST_REQUESTS = 'requests',

  ADMIN_CONTACT_REQUESTS = 'contact-requests',

  ADMIN_SETTINGS = 'settings',

  ADMIN_SENTRY_DEBUG = 'debug/sentry',
  ADMIN_MIGRATION_DEBUG = 'debug/migration',
  ADMIN_REQUEST_DEBUG = 'debug/request',
}

export enum jobseekerRoutes {
  JOBSEEKER_HOME = 'jobseeker',

  JOBSEEKER_CHAT = 'messages',
  JOBSEEKER_CHAT_VIEW = 'messages/:id',

  JOBSEEKER_SETTINGS = 'settings',

  JOBSEEKER_APPLICATIONS = 'applications',
  JOBSEEKER_SAVED = 'saved',
}

export type Routes = typeof routes;

export const routes = {
  ...noAuthRoutes,
  ...adminRoutes,
  ...employerRoutes,
  ...jobseekerRoutes,
};

export const routerOptions = {
  future: {
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_relativeSplatPath: true,
    v7_skipActionErrorRevalidation: true,
    v7_startTransition: true,
  },
};

export const noAuthRouter = [
  {
    index: true,
    element: <Home />,
  },
  {
    path: routes.JOBSEEKERS,
    element: <JobSearch />,
  },
  {
    path: routes.JOB_VIEW,
    element: <JobSearch />,
  },
  {
    path: routes.EMPLOYERS,
    element: <Employers />,
  },
  {
    path: 'login',
    element: <Navigate to="/auth/login" replace />,
  },
  {
    path: routes.LOGIN,
    element: <Login />,
  },
  {
    path: routes.JOBSEEKER_SIGNUP,
    element: <CreateJobseeker />,
  },
  {
    path: routes.EMPLOYER_SIGNUP,
    element: <CreateEmployer />,
  },
  {
    path: routes.ROOFTOP,
    element: <Rooftop />,
  },
  {
    path: routes.FORGOT_PASSWORD,
    element: <ForgotPassword />,
  },
  {
    path: routes.TERMS,
    element: <Terms />,
  },
  {
    path: routes.PRIVACY,
    element: <Privacy />,
  },
  {
    path: routes.GO_EXCELSUS,
    element: <GoExcelsus />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export const employerRouter = [
  {
    index: true,
    element: <EmployerHome />,
  },
  {
    path: employerRoutes.EMPLOYER_SETTINGS,
    element: <EmployerSettings />,
  },
  {
    path: routes.EMPLOYER_JOBS_INDEX,
    element: <EmployerJobIndex />,
  },
  {
    path: routes.EMPLOYER_POST_A_JOB,
    element: <EmployerJobAdd />,
  },
  {
    path: routes.EMPLOYER_JOBS_ADD,
    element: <EmployerJobAdd />,
  },
  {
    path: routes.EMPLOYER_JOBS_VIEW,
    element: <EmployerJobView />,
  },
  {
    path: routes.EMPLOYER_CANDIDATES_INDEX,
    element: <EmployerJobseekerIndex />,
  },
  {
    path: routes.EMPLOYER_CANDIDATES_VIEW,
    element: <EmployerJobseekerView />,
  },
  {
    path: routes.EMPLOYER_CANDIDATES_RESUME_VIEW,
    element: <EmployerJobseekerResumeView />,
  },
  {
    path: routes.EMPLOYER_POSTS_INDEX,
    element: <EmployerPostIndex />,
  },
  {
    path: routes.EMPLOYER_POSTS_VIEW,
    element: <EmployerPostView />,
  },
  {
    path: routes.EMPLOYER_APPLICATIONS_VIEW,
    element: <ApplicationView />,
  },
  {
    path: routes.EMPLOYER_REPORTS,
    element: <p>reports</p>,
  },
  {
    path: routes.EMPLOYER_ROOFTOPS_INDEX,
    element: <EmployerRooftopIndex />,
  },
  {
    path: routes.EMPLOYER_ROOFTOPS_VIEW,
    element: <EmployerRooftopView />,
  },
  {
    path: routes.EMPLOYER_CHAT,
    element: <EmployerChat />,
  },
  {
    path: routes.EMPLOYER_CHAT_VIEW,
    element: <EmployerChat />,
  },
  {
    path: routes.EMPLOYER_DEMO_POST_A_JOB,
    element: <EmployerDemo idReplacement="Post a Job" videoUrl={VIDEO_URLS.DEMO_POST_A_JOB} />,
  },
  {
    path: routes.EMPLOYER_DEMO_REQUEST_RECRUITER_ASSISTANCE,
    element: (
      <EmployerDemo idReplacement="Request a Recruiter" videoUrl={VIDEO_URLS.DEMO_REQUEST_RECRUITER_ASSISTANCE} />
    ),
  },
  {
    path: routes.EMPLOYER_DEMO_MANAGE_CANDIDATES,
    element: <EmployerDemo idReplacement="Manage Candidates" videoUrl={VIDEO_URLS.DEMO_MANAGE_CANDIDATES} />,
  },
];

export const adminRouter = [
  {
    index: true,
    element: <AdminHome />,
  },
  {
    path: routes.ADMIN_JOBS,
    element: <JobIndex />,
  },
  {
    path: routes.ADMIN_JOBS_VIEW,
    element: <JobView />,
  },
  {
    path: routes.ADMIN_EMPLOYERS,
    element: <EmployerIndex />,
  },
  {
    path: routes.ADMIN_EMPLOYERS_VIEW,
    element: <EmployerView />,
  },
  {
    path: routes.ADMIN_JOBSEEKERS,
    element: <JobseekerIndex />,
  },
  {
    path: routes.ADMIN_JOBSEEKERS_ADD,
    element: <p>jobseeker add</p>,
  },
  {
    path: routes.ADMIN_JOBSEEKERS_VIEW,
    element: <JobseekerView />,
  },
  {
    path: routes.ADMIN_CAMPAIGNS,
    element: <CampaignIndex />,
  },
  {
    path: routes.ADMIN_CAMPAIGNS_VIEW,
    element: <CampaignView />,
  },
  {
    path: routes.ADMIN_ROOFTOPS,
    element: <RooftopIndex />,
  },
  {
    path: routes.ADMIN_ROOFTOPS_VIEW,
    element: <RooftopView />,
  },
  {
    path: routes.ADMIN_REPORTS,
    element: <p>screening reports</p>,
  },
  {
    path: routes.ADMIN_RESUMES,
    element: <ResumeIndex />,
  },
  {
    path: routes.ADMIN_RESUMES_VIEW,
    element: <ResumeView />,
  },
  {
    path: routes.ADMIN_PROMOS,
    element: <PromoIndex />,
  },
  {
    path: routes.ADMIN_PROMOS_VIEW,
    element: <PromoView />,
  },
  {
    path: routes.ADMIN_POSTS,
    element: <PostIndex />,
  },
  {
    path: routes.ADMIN_POSTS_VIEW,
    element: <PostView />,
  },
  {
    path: routes.ADMIN_POSTS_ADD,
    element: <PostAdd />,
  },
  {
    path: routes.ADMIN_APPLICATIONS_VIEW,
    element: <ApplicationView isAdmin />,
  },
  {
    path: routes.ADMIN_SETTINGS,
    element: <SettingsIndex />,
  },
  {
    path: routes.ADMIN_POST_REQUESTS,
    element: <PostRequestIndex />,
  },
  {
    path: routes.ADMIN_CONTACT_REQUESTS,
    element: <ContactRequestIndex />,
  },
  {
    path: routes.ADMIN_SENTRY_DEBUG,
    element: <SentryDebug />,
  },
  {
    path: routes.ADMIN_MIGRATION_DEBUG,
    element: <MigrationDebug />,
  },
  {
    path: routes.ADMIN_REQUEST_DEBUG,
    element: <RequestDebug />,
  },
];

export const jobseekerRouter = [
  {
    index: true,
    element: <JobseekerProfile />,
  },
  {
    path: routes.JOBSEEKER_CHAT,
    element: <JobseekerChat />,
  },
  {
    path: routes.JOBSEEKER_CHAT_VIEW,
    element: <JobseekerChat />,
  },
  {
    path: routes.JOBSEEKER_SETTINGS,
    element: <JobseekerSettings />,
  },
  {
    path: routes.JOBSEEKER_APPLICATIONS,
    element: <JobseekerApplications />,
  },
  {
    path: routes.JOBSEEKER_SAVED,
    element: <JobseekerSavedPosts />,
  },
];

export const router = sentryRouter(
  [
    {
      path: routes.HOME,
      element: <AppShell />,
      children: [
        {
          path: '',
          element: <DefaultShell />,
          children: noAuthRouter,
          errorElement: <DefaultErrorBoundary />,
        },
        {
          path: routes.ADMIN_HOME,
          element: <DashboardShell />,
          children: adminRouter,
          errorElement: <DashboardErrorBoundary />,
        },
        {
          path: routes.EMPLOYER_HOME,
          element: <DashboardShell />,
          children: employerRouter,
          errorElement: <DashboardErrorBoundary />,
        },
        {
          path: routes.JOBSEEKER_HOME,
          element: <DefaultShell />,
          children: jobseekerRouter,
          errorElement: <DefaultErrorBoundary />,
        },
      ],
    },
  ],
  routerOptions,
);

export default router;
